import { Fragment } from 'react'

import { Icon, type IconName } from './ui'

/**
 * `GroupHeader` + `SummaryLine` — the week, month and "Your usual" headers of
 * History's Journal view (Sprint 1.9.11 J2, concept B2/B3).
 *
 * PORTED from `brainspot-design-system/project/components/history/GroupHeader.jsx`
 * by that concept's §4 component map: the app's own `Icon`, the app's own CSS,
 * and no `window.BSIcon`. Every visual value below is the prototype's; the
 * mechanism is the one `components.css` already uses for every other primitive
 * it re-implemented from that system. Three values could not come across
 * literally and are named where they changed:
 *
 *  - The DS paints the coverage line and the unavailable sentence in `--faint`.
 *    `styles/tokens.test.ts` refuses `--faint` as a text colour on these
 *    surfaces (3.5-4.2:1 dark, 2.2-2.6:1 light), so both are `--muted`. A
 *    coverage line is the one thing on the header a reader has to be able to
 *    read.
 *  - The DS's `14.5px` title and `11.5px` coverage are not on the app's type
 *    scale, which `audit:css` check F enforces and UI-LAYOUT-01 raised one
 *    step for readability. They become `--fs-h3` and `--fs-label` — the tokens
 *    documented for exactly these two roles ("sub-head inside a card", "mono
 *    label").
 *  - `--white` and `--cyan` are written as `--text-primary` and
 *    `--text-accent`, the contrast-corrected aliases the rest of
 *    `components.css` reaches for. `tokens.test.ts` fails a raw accent used as
 *    text.
 *
 * ── IT GROUPS; IT NEVER GRADES ──────────────────────────────────────────────
 *
 * There is no tone, no arrow, no delta and no colour-by-value here, and the
 * component has no prop that could carry one. `none` dims an ICON, and means
 * only "the answer was none" — the concept's own word. The six facts arrive as
 * formatted text from `journal-summary`, in the one order the design shows
 * them; nothing in this file computes, compares or ranks anything.
 */

export interface SummaryFact {
  /** Labels the fact. Decoration beside text that already says what it means. */
  icon?: IconName
  /**
   * The fact, as the backend formatted it ("7.0 h", "2 of 4 days"). A STRING,
   * because `brainspot/contracts/journal_summary.py` is explicit that the
   * producer rounds and the client only formats — a number here would invite
   * the arithmetic `clientScience.test.ts` exists to forbid.
   */
  text: string
  /** Dim the icon: the answer was "none" / zero. Never a colour, never a tone. */
  none?: boolean
}

export interface SummaryLineProps {
  facts: SummaryFact[]
  /** The whole line in the quieter voice — the "no summary" sentence. */
  muted?: boolean
}

/**
 * The DS's default, and the words the spec names.
 *
 * It is the component's FALLBACK and no longer the app's answer: both call
 * sites now pass `unavailableText` read from the payload's own
 * `unavailable_reason` (`history/journalFacts.ts`), because this sentence is a
 * measurement and was being shown for every empty line -- including a group
 * with plenty of journals and nothing answered in them. Kept because a header
 * rendered with neither facts nor a reason must still say something, and a
 * ported DS component keeps its own default.
 */
const NO_SUMMARY = 'Too few journals for a summary'

export function SummaryLine({ facts, muted = false }: SummaryLineProps) {
  return (
    <span className={['summary-line', muted ? 'summary-line--muted' : null].filter(Boolean).join(' ')}>
      {facts.map((fact, index) => (
        // Keyed by index, as the DS does, and here that is the stable key
        // rather than the lazy one: this is a FIXED list of six facts in a
        // fixed order, so position is the identity. There is nothing else to
        // key on — two facts can share an icon and a text.
        <Fragment key={index}>
          {index > 0 && (
            // `aria-hidden`, so a screen reader reads six phrases and not six
            // phrases interleaved with "middle dot".
            <i className="summary-line__sep" aria-hidden="true">
              ·
            </i>
          )}
          {fact.icon && (
            <Icon
              name={fact.icon}
              className={['summary-line__icon', fact.none ? 'summary-line__icon--none' : null]
                .filter(Boolean)
                .join(' ')}
            />
          )}
          <span className="summary-line__fact">{fact.text}</span>
        </Fragment>
      ))}
    </span>
  )
}

export interface GroupHeaderProps {
  /** "Week of 28 Sep", "October 2026", "Your usual". */
  title: string
  /** The mono line: "4 journals of 5 recordings · recorded 08:31–09:12". */
  coverage?: string
  /** The six facts in the fixed order. Absent or empty → `unavailableText`. */
  facts?: SummaryFact[]
  /** Shown muted in place of the facts when the payload reports none. */
  unavailableText?: string
  /** The pinned "Your usual" variant: boxed, accent title. */
  pinned?: boolean
}

/**
 * No rest spread, and no `style` prop, which is where this parts company with
 * the prototype's `...rest`. `Icon` set that precedent for the same reason: a
 * caller that can pass `style` can undo any of the decisions above, and the one
 * thing a ported component owes the concept is that it still looks like it.
 * Both call sites want the default; a real second variant earns a real prop.
 */
export function GroupHeader({ title, coverage, facts, unavailableText, pinned = false }: GroupHeaderProps) {
  return (
    <div className={['group-header', pinned ? 'group-header--pinned' : null].filter(Boolean).join(' ')}>
      <div className="group-header__title">{title}</div>
      {coverage !== undefined && coverage !== '' && (
        <div className="group-header__coverage">{coverage}</div>
      )}
      <div className="group-header__summary">
        {facts && facts.length > 0 ? (
          <SummaryLine facts={facts} />
        ) : (
          <SummaryLine facts={[{ text: unavailableText ?? NO_SUMMARY }]} muted />
        )}
      </div>
    </div>
  )
}
